import { getDataLayer } from "@shared/common/utils/gtm";
import crypto from "crypto";
import { IncomingMessage } from "http";
import { customAlphabet } from "nanoid";
import {
  getCsrfToken,
  signIn,
  SignInOptions,
  SignInResponse,
} from "next-auth/react";

export const getRedirectUrl = (
  nextAuthUrl: string,
  nextAuthBaseUrl: string
) => {
  const urlObj = new URL(nextAuthUrl, nextAuthBaseUrl);
  // embed.codenbutter.com 에서 특정 파라미터 있을 경우 /v2/:organization/:site/popup로 리다이렉트
  if (process.env.NEXT_PUBLIC_ENABLE_EMBED) {
    const organization = urlObj.searchParams.get("organizationId");
    const siteId = urlObj.searchParams.get("siteId");
    const language = urlObj.searchParams.get("language");
    const languagePrefix = language === "en" ? "/en" : "";
    if (organization && siteId) {
      return `${nextAuthBaseUrl}${languagePrefix}/v2/${organization}/${siteId}/popup`;
    } else {
      return `${nextAuthBaseUrl}${languagePrefix}/v2/login`;
    }
  }

  return urlObj.href;
};

type ProviderType =
  | "email"
  | "github"
  | "google"
  | "naver"
  | "cafe24"
  | "password"
  | "kakao";

export const handleLoginClick = async (
  provider: ProviderType,
  option: SignInOptions = { callbackUrl: "/" }
): Promise<SignInResponse | undefined> => {
  getDataLayer().push({
    event: "click_login_button",
    method: provider,
  });

  return await signIn(provider, option);
};

export const get6NumbersToken = () => {
  return `${customAlphabet("123456789", 1)()}${customAlphabet(
    "1234567890",
    5
  )()}`;
};

export const encryptToken = (token: string, secret: string) => {
  const sha256Token = crypto
    .createHash("sha256")
    .update(token)
    .update(secret)
    .digest("hex");

  return sha256Token;
};

/**
 * https://github.com/nextauthjs/next-auth/issues/7166#issuecomment-1537495364
 * next-auth에서 getCsrfToken() 함수의 사용 방법을 ({ req })와 같은 방식으로 안내하고 있지만,
 * 실제 위와 같은 형태로 이용할 경우 JSON 토큰 파서 에러가 발생.
 * 위 이슈 답변을 참조하여 headers 값을 명시적으로 입력하여 처리합니다.
 */
export const getCsrfTokenFromPostRequest = async (
  req?: Partial<IncomingMessage> & {
    body?: unknown;
  }
) => {
  return getCsrfToken({
    req: {
      headers: req?.headers,
    },
  });
};

export enum ProviderIdEnum {
  github = 1,
  google = 2,
  naver = 3,
  kakao = 4,
}

export enum ProviderTypeEnum {
  oauth = 1,
}

export const getProviderIdByName = (providerName: string) => {
  if (providerName === "github") {
    return ProviderIdEnum.github;
  } else if (providerName === "google") {
    return ProviderIdEnum.google;
  } else if (providerName === "naver") {
    return ProviderIdEnum.naver;
  } else if (providerName === "kakao") {
    return ProviderIdEnum.kakao;
  }

  return -1;
};

export const getProviderTypeByName = (providerType: string) => {
  if (providerType === "oauth") {
    return ProviderTypeEnum.oauth;
  }

  return -1;
};

export const getProviderCode = (
  providerType: ProviderTypeEnum,
  providerId: ProviderIdEnum
) => {
  if (providerType === ProviderTypeEnum.oauth) {
    if (providerId === ProviderIdEnum.github) {
      return "github";
    } else if (providerId === ProviderIdEnum.google) {
      return "google";
    } else if (providerId === ProviderIdEnum.naver) {
      return "naver";
    } else if (providerId === ProviderIdEnum.kakao) {
      return "kakao";
    }
  }

  return "unknown";
};

export const getProviderHangulNameByProviderName = (
  providerName: string | null | undefined
) => {
  if (providerName === "github") {
    return "GitHub";
  } else if (providerName === "google") {
    return "구글";
  } else if (providerName === "naver") {
    return "네이버";
  } else if (providerName === "kakao") {
    return "카카오";
  }

  return "알수없음";
};

export const convertToE164 = (
  countryCode: string,
  phoneNumber: string
): string => {
  // 전화번호에서 비숫자 문자를 제거합니다.
  let cleanPhoneNumber = phoneNumber.replace(/\D/g, "");

  // 국가 코드 다음에 오는 '0'을 제거합니다 (국내 접속 번호 제거).
  if (cleanPhoneNumber.startsWith("0")) {
    cleanPhoneNumber = cleanPhoneNumber.substring(1);
  }

  return `+${countryCode}${cleanPhoneNumber}`;
};
